(() => {
  'use strict';
  const root = document.body.dataset.root || '/';
  const path = suffix => `${root.endsWith('/') ? root : `${root}/`}${suffix}`;
  const strings = document.getElementById('ThemeStrings')?.dataset || {};
  const status = message => { const node = document.getElementById('StoreStatus'); if (node) node.textContent = message; };
  const openDialog = dialog => { if (dialog && !dialog.open) dialog.showModal(); };
  const closeDialog = dialog => { if (dialog?.open) dialog.close(); };
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => reducedMotion() ? 'instant' : 'smooth';
  const parseHTML = html => new DOMParser().parseFromString(html, 'text/html');
  const getHTML = async (url, signal) => { const response = await fetch(url, {signal}); if (!response.ok) throw new Error(strings.error); return parseHTML(await response.text()); };

  class StoreHeader extends HTMLElement {
    connectedCallback() {
      this.observer = new ResizeObserver(() => document.documentElement.style.setProperty('--header-height', `${this.getBoundingClientRect().height}px`));
      this.observer.observe(this);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  customElements.define('store-header', StoreHeader);

  class FooterNavigation extends HTMLElement {
    connectedCallback() {
      this.media = matchMedia('(min-width: 750px)');
      this.sync = () => this.querySelectorAll('details').forEach(detail => { detail.open = this.media.matches; });
      this.sync();
      this.media.addEventListener?.('change', this.sync);
    }
    disconnectedCallback() { this.media?.removeEventListener?.('change', this.sync); }
  }
  customElements.define('footer-navigation', FooterNavigation);


  class StoreCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.move(1), options);
      this.track.addEventListener('scroll', () => this.update(), {...options, passive: true});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1); }
      }, options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.update();
    }
    move(direction) { this.track.scrollBy({left: direction * this.track.clientWidth * .85, behavior: behavior()}); }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const previous = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (previous) previous.disabled = this.track.scrollLeft < 2;
      if (next) next.disabled = this.track.scrollLeft >= max - 2;
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
  }
  customElements.define('store-carousel', StoreCarousel);

  class ProductDetail extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.track = this.querySelector('[data-gallery]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = Math.max(0, this.slides.findIndex(slide => slide.dataset.mediaId === this.dataset.media));
      const options = {signal: this.abort.signal};
      this.addEventListener('change', event => { if (event.target.matches('[data-option-value]')) this.changeVariant(event.target); }, options);
      this.querySelector('[data-product-form]')?.addEventListener('submit', event => this.addToCart(event), options);
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showImage(this.index - 1), options);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showImage(this.index + 1), options);
      this.querySelectorAll('[data-thumbnail]').forEach(button => button.addEventListener('click', () => this.showImage(Number(button.dataset.thumbnail)), options));
      this.track?.addEventListener('scroll', () => {
        this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
        this.updateGallery();
      }, {...options, passive: true});
      this.track?.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.showImage(this.index + (event.key === 'ArrowRight' ? 1 : -1)); }
      }, options);
      this.querySelector('[data-zoom]')?.addEventListener('click', () => {
        const slide = this.slides[this.index];
        if (!slide?.dataset.zoomSrc) return;
        const image = this.querySelector('[data-zoom-image]');
        image.src = slide.dataset.zoomSrc;
        image.alt = slide.dataset.zoomAlt || this.querySelector('h1').textContent;
        openDialog(this.querySelector('dialog'));
      }, options);
      this.querySelector('[data-description-toggle]')?.addEventListener('click', event => {
        const button = event.currentTarget;
        const container = button.closest('[data-description]');
        const expanded = !container.hasAttribute('data-expanded');
        container.toggleAttribute('data-expanded', expanded);
        container.querySelector('[data-description-preview]').hidden = expanded;
        container.querySelector('[data-description-full]').hidden = !expanded;
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? button.dataset.less : button.dataset.more;
      }, options);
      this.resizeObserver = new ResizeObserver(() => {
        const purchase = this.querySelector('[data-purchase]');
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 150;
        purchase.classList.toggle('is-sticky', innerWidth >= 750 && purchase.scrollHeight < innerHeight - headerHeight - 48);
      });
      this.resizeObserver.observe(this.querySelector('[data-purchase]'));
      this.resizeObserver.observe(document.documentElement);
      this.showImage(this.index, true);
      const description = this.querySelector('[data-description]');
      const toggle = description?.querySelector('[data-description-toggle]');
      if (toggle) {
        const full = description.querySelector('[data-description-full]');
        const preview = description.querySelector('[data-description-preview]');
        const long = full.textContent.trim().split(/\s+/).length > 65 || !!full.querySelector('img,details,table');
        preview.hidden = !long;
        full.hidden = long;
        toggle.hidden = !long;
      }
      syncFavorites();
    }
    showImage(index, instant = false) {
      if (!this.slides.length || !this.track) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      this.track.scrollTo({left: this.index * this.track.clientWidth, behavior: instant ? 'instant' : behavior()});
      this.updateGallery();
    }
    updateGallery() {
      const counter = this.querySelector('[data-counter]');
      if (counter) counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      this.querySelectorAll('[data-thumbnail]').forEach((button, index) => button.setAttribute('aria-current', String(index === this.index)));
      const previous = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (previous) previous.disabled = this.index === 0;
      if (next) next.disabled = this.index >= this.slides.length - 1;
      const zoom = this.querySelector('[data-zoom]');
      if (zoom) zoom.hidden = !this.slides[this.index]?.dataset.zoomSrc;
      this.slides.forEach((slide, index) => { if (index !== this.index) slide.querySelectorAll('video').forEach(video => video.pause()); });
    }
    async changeVariant(input) {
      if (this.hasAttribute('data-preview-product')) {
        const values = [...this.querySelectorAll('[data-option-value]:checked')].map(option => option.value);
        this.dispatchEvent(new CustomEvent('preview-variant', {bubbles:true, detail:{values,focusValue:input.value}}));
        return;
      }
      this.variantRequest?.abort();
      this.variantRequest = new AbortController();
      const controller = this.variantRequest;
      const values = [...this.querySelectorAll('[data-option-value]:checked')].map(option => option.value);
      const url = new URL(input.dataset.productUrl || this.dataset.url, location.origin);
      url.searchParams.set('option_values', values.join(','));
      url.searchParams.set('section_id', this.dataset.section);
      const submit = this.querySelector('[name=add]');
      submit.disabled = true;
      this.setAttribute('aria-busy', 'true');
      const quantity = this.querySelector('[name=quantity]')?.value;
      const focusValue = input.value;
      try {
        const doc = await getHTML(url, controller.signal);
        if (controller.signal.aborted) return;
        const replacement = doc.querySelector('product-detail');
        if (!replacement || replacement.dataset.productId !== this.dataset.productId) {
          if (replacement) { url.searchParams.delete('section_id'); location.assign(url); return; }
          throw new Error(strings.error);
        }
        const newQuantity = replacement.querySelector('[name=quantity]');
        if (newQuantity && quantity) {
          const min = Number(newQuantity.min) || 1;
          const step = Number(newQuantity.step) || 1;
          const max = Number(newQuantity.max) || Infinity;
          newQuantity.value = String(Math.max(min, Math.min(max, min + Math.floor((Number(quantity) - min) / step) * step)));
        }
        const variantId = replacement.dataset.variantId;
        const newUrl = new URL(location.href);
        newUrl.searchParams.delete('option_values');
        newUrl.searchParams.delete('variant');
        if (variantId) newUrl.searchParams.set('variant', variantId);
        else newUrl.searchParams.set('option_values', values.join(','));
        history.replaceState({}, '', newUrl);
        this.replaceWith(replacement);
        const selectedInput = [...replacement.querySelectorAll('[data-option-value]')].find(option => option.value === focusValue);
        selectedInput?.focus({preventScroll: true});
        status(replacement.querySelector('.availability')?.textContent || '');
      } catch (error) {
        if (error.name !== 'AbortError') {
          const message = this.querySelector('[data-product-error]');
          message.textContent = strings.error;
          message.hidden = false;
          status(strings.error);
        }
      } finally { if (this.isConnected && !controller.signal.aborted) this.removeAttribute('aria-busy'); }
    }
    async addToCart(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const button = form.querySelector('[name=add]');
      if (this.hasAttribute('data-preview-product') || button.disabled || this.hasAttribute('aria-busy') || !form.reportValidity()) return;
      const label = button.textContent;
      const message = this.querySelector('[data-product-error]');
      button.disabled = true;
      button.textContent = strings.loading;
      message.hidden = true;
      try {
        const response = await fetch(path('cart/add.js'), {method: 'POST', body: new FormData(form), headers: {Accept: 'application/json'}});
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || strings.error);
        status(strings.added);
        try { await refreshCart(true); } catch { location.assign(document.body.dataset.cartUrl || path('cart')); }
      } catch (error) { message.textContent = error.message || strings.error; message.hidden = false; }
      finally { button.disabled = false; button.textContent = label; }
    }
    disconnectedCallback() { this.abort?.abort(); this.variantRequest?.abort(); this.resizeObserver?.disconnect(); this.descriptionObserver?.disconnect(); }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      this.observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { this.load(); this.observer.disconnect(); } }, {rootMargin: '400px'});
      this.observer.observe(this);
    }
    async load() {
      try { const doc = await getHTML(this.dataset.url); const content = doc.querySelector('[data-recommendations-content]'); if (content) { this.replaceWith(content); syncFavorites(); } else if (!this.querySelector('[data-recommendations-fallback]')) this.remove(); }
      catch { if (!this.querySelector('[data-recommendations-fallback]')) this.remove(); }
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.input = this.querySelector('input[type=search]');
      this.output = this.querySelector('#SearchSuggestions');
      this.input.addEventListener('input', () => { clearTimeout(this.timer); this.request?.abort(); if (this.input.value.trim().length < 2) { this.output.replaceChildren(); this.input.setAttribute('aria-expanded', 'false'); return; } this.timer = setTimeout(() => this.search(), 180); }, options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') { const first = this.output.querySelector('a'); if (first) { event.preventDefault(); first.focus(); } }
      }, options);
    }
    async search() {
      if (window.HemtonPreview) {
        const query=this.input.value.trim();
        try { const content=await window.HemtonPreview.suggestions(query); if(query!==this.input.value.trim()) return; this.output.replaceChildren(content); this.input.setAttribute('aria-expanded','true'); }
        catch { this.output.textContent=strings.error; this.input.setAttribute('aria-expanded','false'); }
        return;
      }
      this.request?.abort();
      this.request = new AbortController();
      const url = new URL(path('search/suggest'), location.origin);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('q', this.input.value.trim());
      url.searchParams.set('resources[type]', 'product,collection,page,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      try { const doc = await getHTML(url, this.request.signal); const content = doc.querySelector('[data-suggestions]'); this.output.replaceChildren(...(content ? [content] : [])); this.input.setAttribute('aria-expanded', String(!!content)); }
      catch (error) { if (error.name !== 'AbortError') { this.output.textContent = strings.error; this.input.setAttribute('aria-expanded', 'false'); } }
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); clearTimeout(this.timer); }
  }
  customElements.define('predictive-search', PredictiveSearch);

  async function refreshCart(open = false) {
    const doc = await getHTML(`${document.body.dataset.cartUrl || path('cart')}?section_id=cart-drawer`);
    const incoming = doc.querySelector('#CartDrawer');
    const drawer = document.getElementById('CartDrawer');
    if (!incoming || !drawer) { location.assign(document.body.dataset.cartUrl || path('cart')); return; }
    drawer.innerHTML = incoming.innerHTML;
    const count = incoming.dataset.count;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; node.hidden = count === '0'; });
    if (open) openDialog(drawer);
  }

  const storageKey = 'hemton_preferences_v1';
  let saved = {enabled: false, favorites: [], recent: []};
  const validHandles = values => Array.isArray(values) ? values.filter(value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(value)).slice(0,24) : [];
  try { const value = JSON.parse(localStorage.getItem(storageKey)); if (value?.enabled === true) saved = {enabled: true, favorites: validHandles(value.favorites), recent: validHandles(value.recent).slice(0,8)}; } catch {}
  let pendingFavorite = null;
  function persist() {
    try { localStorage.setItem(storageKey, JSON.stringify(saved)); return true; }
    catch { status(strings.storageError); document.querySelector('[data-preferences-status]').textContent = strings.storageError; return false; }
  }
  function syncFavorites() { document.querySelectorAll('[data-favorite]').forEach(button => button.setAttribute('aria-pressed', String(saved.enabled && saved.favorites.includes(button.dataset.favorite)))); }
  function toggleFavorite(handle) {
    if (!saved.enabled) { pendingFavorite = handle; openDialog(document.getElementById('PreferencesDialog')); return; }
    const index = saved.favorites.indexOf(handle);
    if (index < 0) saved.favorites = [handle,...saved.favorites].slice(0,24);
    else saved.favorites.splice(index,1);
    persist(); syncFavorites(); status(index < 0 ? strings.saved : strings.removed);
    if (document.getElementById('FavoritesDialog').open) renderFavorites();
  }
  async function getCards(handles) {
    if (document.getElementById('HemtonCatalogConfig') && !window.HemtonPreview && handles.some(handle=>handle.startsWith('hemton2-'))) {
      await new Promise(resolve=>{ const timer=setTimeout(resolve,4000); document.addEventListener('hemton:preview-ready',()=>{clearTimeout(timer);resolve();},{once:true}); });
    }
    const results = await Promise.allSettled(handles.map(async handle => {
      if (window.HemtonPreview && handle.startsWith('hemton2-')) {
        const html=await window.HemtonPreview.getCard(handle);
        return html ? parseHTML(html).querySelector('.product-card') : null;
      }
      const doc = await getHTML(`${path('products/')}${encodeURIComponent(handle)}?section_id=product-card-fragment`);
      return doc.querySelector('.product-card');
    }));
    return results.flatMap(result => result.status === 'fulfilled' && result.value ? [result.value] : []);
  }
  let favoritesRevision = 0;
  async function renderFavorites() {
    const revision = ++favoritesRevision;
    const output = document.querySelector('[data-favorites-content]');
    output.className = 'product-grid';
    output.textContent = strings.loading;
    if (!saved.enabled || !saved.favorites.length) { output.textContent = strings.emptyFavorites; return; }
    const cards = await getCards(saved.favorites);
    if (revision !== favoritesRevision) return;
    output.replaceChildren(...cards);
    if (!cards.length) output.textContent = strings.emptyFavorites;
    syncFavorites();
  }
  let recentRevision = 0;
  async function renderRecent() {
    const revision = ++recentRevision;
    const tracks = [...document.querySelectorAll('[data-recent-track]')];
    if (!tracks.length) return;
    const handles = saved.enabled ? saved.recent.filter(handle => handle !== document.body.dataset.productHandle) : [];
    if (!handles.length) { document.querySelectorAll('[data-recent]').forEach(section => { section.hidden = true; section.querySelector('[data-recent-track]').replaceChildren(); }); return; }
    const cards = await getCards(handles);
    if (revision !== recentRevision || !saved.enabled) return;
    tracks.forEach(track => { track.replaceChildren(...cards.map(card => card.cloneNode(true))); track.closest('[data-recent]').hidden = !cards.length; track.closest('store-carousel')?.update(); });
    syncFavorites();
  }
  function recordRecent() {
    const handle = document.body.dataset.productHandle;
    if (!saved.enabled || !handle || !validHandles([handle]).length) return;
    saved.recent = [handle,...saved.recent.filter(item => item !== handle)].slice(0,8);
    persist();
  }
  document.addEventListener('click', async event => {
    const target = event.target.closest('button,a,dialog');
    if (!target) return;
    if (target.matches('[data-open]')) {
      event.preventDefault();
      const dialog = document.getElementById(target.dataset.open);
      openDialog(dialog);
      if (target.dataset.open === 'FavoritesDialog') renderFavorites();
      if (target.dataset.open === 'SearchDialog') dialog.querySelector('input[type=search]')?.focus();
    }
    if (target.matches('[data-close]')) closeDialog(target.closest('dialog'));
    if (target.matches('dialog') && event.target === target) {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(target);
    }
    if (target.matches('[data-open-cart]')) { event.preventDefault(); try { await refreshCart(true); } catch { location.assign(target.href); } }
    if (target.matches('[data-favorite]')) toggleFavorite(target.dataset.favorite);
    if (target.matches('[data-enable-preferences]')) {
      saved.enabled = true;
      if (!persist()) { saved.enabled = false; return; }
      recordRecent();
      document.querySelector('[data-preferences-status]').textContent = strings.enabled;
      if (pendingFavorite) { toggleFavorite(pendingFavorite); pendingFavorite = null; }
      syncFavorites(); renderRecent();
    }
    if (target.matches('[data-clear-preferences]')) {
      saved = {enabled: false, favorites: [], recent: []}; pendingFavorite = null;
      try { localStorage.removeItem(storageKey); } catch { status(strings.storageError); }
      favoritesRevision++; syncFavorites(); renderRecent();
      document.querySelector('[data-preferences-status]').textContent = strings.cleared;
      if (document.getElementById('FavoritesDialog').open) renderFavorites();
    }
    if (target.matches('[data-increase],[data-decrease]')) {
      const input = target.closest('[data-quantity]').querySelector('input');
      if (target.matches('[data-increase]')) input.stepUp(); else input.stepDown();
      input.dispatchEvent(new Event('change',{bubbles:true}));
    }
  });
  document.addEventListener('toggle', event => { if (event.target.matches?.('.nav-disclosure[open]')) document.querySelectorAll('.nav-disclosure[open]').forEach(details => { if (details !== event.target) details.open = false; }); }, true);
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.nav-disclosure[open]').forEach(details => details.open = false); });
  document.addEventListener('click', event => { if (!event.target.closest('.nav-disclosure')) document.querySelectorAll('.nav-disclosure[open]').forEach(details => details.open = false); });
  document.addEventListener('shopify:section:load', () => { syncFavorites(); renderRecent(); });
  document.addEventListener('hemton:preview-product', event => { document.body.dataset.productHandle=event.detail.handle; syncFavorites(); recordRecent(); renderRecent(); });
  customElements.define('product-detail', ProductDetail);
  syncFavorites(); recordRecent(); renderRecent();
})();
